import { NodeClient } from '@amplitude/node';
import { AmplitudeClient } from 'amplitude-js';
import Router from 'next/router';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import { PaymentType } from '../../types/PaymentTypes';
import publicRuntimeConfig from '../config';
import { EXCLUDE_FROM_TRACKING } from '../constants/excludeFromTracking';
import isBrowser from '../helpers/isBrowser';
import { getNetworkInfo } from '../helpers/network';
import * as ga from '../tracking/googleAnalyticsTracking';
import { captureException, captureMessage, SentryError } from '../tracking/sentry/sentry';
import { ADJUST_EVENTS, AmplitudeEvent, EVENTS, EVENT_NAME } from './events';
import { getUtmTagsFromQuery } from './utmTags';
// Re-exporting for accessibility reasons
export { ADJUST_EVENTS, EVENTS, EVENT_NAME } from './events';

type State = {
  amplitude?: AmplitudeClient;
  amplitudeProxy?: AmplitudeClient; // temporary experiment to bypass adblockers
  shouldQueue: boolean;
  queue: (
    | { name: 'sendEvent'; params: Parameters<typeof sendEvent> }
    | { name: 'setUserProperties'; params: Parameters<typeof setUserProperties> }
    | { name: 'sendEventTYPED'; params: Parameters<typeof sendEventTYPED> }
    | { name: 'sendEventProxy'; params: Parameters<typeof sendEventProxy> }
  )[];
};

const state: State = {
  shouldQueue: process.env.RUNTIME_ENV === 'browser',
  queue: [],
};

export function setupAmplitude() {
  if (process.env.RUNTIME_ENV === 'browser' && process.env.NODE_ENV !== 'test') {
    import('amplitude-js').then((Amplitude) => {
      const amplitude = Amplitude.getInstance();

      state.amplitude = amplitude;
      amplitude.init(
        publicRuntimeConfig.AMPLITUDE_KEY,
        undefined,
        {
          includeReferrer: true,
          includeUtm: true,
          includeGclid: true,
          trackingOptions: {
            city: false,
            country: true,
            carrier: false,
            device_manufacturer: false,
            device_model: false,
            dma: false,
            ip_address: false,
            language: false,
            os_name: true,
            os_version: true,
            platform: true,
            region: false,
            version_name: true,
          },
        },
        (data) => {
          console.log('amplitude init done'); // eslint-disable-line no-console
          sendAdjustMatchEvent(data.options.deviceId);
        },
      );

      const amplitudeProxy = Amplitude.getInstance('proxy');
      state.amplitudeProxy = amplitudeProxy;
      amplitudeProxy.init(
        publicRuntimeConfig.AMPLITUDE_KEY,
        undefined,
        {
          apiEndpoint: `${window.location.host}/amplitude`,
          forceHttps: false, // DEV compatibility
          includeReferrer: true,
          includeUtm: true,
          includeGclid: true,
          trackingOptions: {
            city: false,
            country: true,
            carrier: false,
            device_manufacturer: false,
            device_model: false,
            dma: false,
            ip_address: false,
            language: false,
            os_name: true,
            os_version: true,
            platform: true,
            region: false,
            version_name: true,
          },
        },
        () => {
          console.log('amplitude proxy init done'); // eslint-disable-line no-console
        },
      );

      state.queue.forEach((item) => {
        switch (item.name) {
          case 'sendEvent':
            sendEvent(...item.params);
            return;
          case 'sendEventTYPED':
            sendEventTYPED(...item.params);
            return;
          case 'sendEventProxy':
            sendEventProxy(...item.params);
            return;
          case 'setUserProperties':
            setUserProperties(...item.params);
            return;
        }
      });
    });
  }
}

const nodeAmplitude = !isBrowser() ? require('@amplitude/node') : null;
let nodeAmplitudeClient: NodeClient;

function setupServerAmplitude() {
  nodeAmplitudeClient = nodeAmplitude.init(publicRuntimeConfig.AMPLITUDE_KEY);
}

if (!isBrowser()) {
  setupServerAmplitude();
}

async function sendAdjustMatchEvent(deviceId?: string) {
  try {
    await fetch('https://api.amplitude.com/2/httpapi', {
      method: 'POST',
      body: JSON.stringify({
        api_key: publicRuntimeConfig.AMPLITUDE_KEY,
        events: [
          {
            device_id: deviceId,
            event_type: 'adjust match event',
          },
        ],
      }),
    });
  } catch (error) {
    captureException(error);
  }
}

const eventsArray = Object.keys(EVENTS).map((key) => EVENTS[key]);

function validateEvent({ name, data }: { name: string; data: unknown }) {
  const tmp = eventsArray.find((tmp) => tmp.name === name);
  if (!tmp) {
    throw new Error(`Event: ${name} not supported. Make sure it is defined in AMPLITUDE_EVENTS`);
  }

  PropTypes.checkPropTypes(tmp.properties, data, 'prop', 'AmplitudeAnalytics');
}

function toLowerCase<T>(value: T): T {
  return value && typeof value === 'string' ? (value.toLowerCase() as unknown as T) : value;
}

export function sendEvent(
  eventName: string | { name: string },
  eventProperties?: Record<string, unknown>,
) {
  const { amplitude, shouldQueue, queue } = state;
  if (!amplitude) {
    if (shouldQueue) {
      queue.push({ name: 'sendEvent', params: [eventName, eventProperties] });
    }
    return;
  }

  let name: string;
  if (typeof eventName === 'object' && eventName.name) {
    // eslint-disable-next-line no-console
    console.log(
      `Looks like your are sending an object as eventName for '${eventName.name}'.\nPlease use the 'name' property to only send the event name`,
    );
    name = eventName.name;
  } else {
    name = eventName as string;
  }

  try {
    validateEvent({
      name,
      data: eventProperties,
    });

    // Make sure all values are in lower case
    const data =
      eventProperties &&
      Object.keys(eventProperties).reduce((data: Record<string, unknown>, key) => {
        const lowerCaseKey = toLowerCase(key);
        data[lowerCaseKey] = toLowerCase(eventProperties[key]);
        return data;
      }, {});

    logAmplitudeEvent(name, data);
    logAdjustEvent(name, data);
    logGAEvent(name, data);

    if (name === EVENTS.logout.name) {
      amplitude.setUserId(null); // Reset user id
      amplitude.regenerateDeviceId(); // Generate a new device id
    }
  } catch (error) {
    // @ts-expect-error FIXME: handle error types explicitly for improved error handling
    captureMessage('trackAmplitude error', error.message);
  }
}

function logAmplitudeEvent(name: string, data?: Record<string, unknown>) {
  state.amplitude?.logEvent(name, { ...data, product: 'milkywire.com' });
}

function logAmplitudeServerEvent(name: string, data?: Record<string, unknown>) {
  nodeAmplitudeClient.logEvent({
    event_type: name,
    event_properties: data,
    device_id: 'ENTERPRISE_SERVER',
    platform: 'Node.js',
  });
}

async function logAdjustEvent(
  name: string,
  data?: { amount?: number; 'currency code'?: string; 'flow type'?: string },
) {
  if (EVENTS.supportSuccessful.name === name && data) {
    const adjust = await import('../adjust');

    if (data['flow type'] === 'gift') {
      adjust.default.trackEvent({
        eventToken: ADJUST_EVENTS.giftPurchaseSuccessful.token,
        revenue: data['amount'],
        currency: data['currency code'],
      });
    } else if (data['flow type'] === 'one time') {
      adjust.default.trackEvent({
        eventToken: ADJUST_EVENTS.oneTimeDonationSuccessful.token,
        revenue: data['amount'],
        currency: data['currency code'],
      });
    } else if (data['flow type'] === 'subscription') {
      adjust.default.trackEvent({
        eventToken: ADJUST_EVENTS.subscriptionPurchaseSuccessful.token,
        revenue: data['amount'],
        currency: data['currency code'],
      });
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function logGAEvent(name: string, data: any) {
  if (EVENTS.startPaymentFlow.name === name && data) {
    if (data['flow type'] === 'gift') {
      ga.beginCheckoutGift({});
    } else if (data['flow type'] === 'one time') {
      ga.beginCheckoutOneTime({
        affiliation: data.initiator,
      });
    } else if (data['flow type'] === 'subscription') {
      ga.beginCheckoutSubscription();
    }
  } else if (EVENTS.supportSuccessful.name === name && data) {
    if (data['flow type'] === 'gift') {
      ga.giftPurchase({
        amount: data['amount'],
        currency: data['currency code'],
        paymentFlowId: data['payment flow id'],
        variant: data['support type'],
      });
    } else if (data['flow type'] === 'one time') {
      ga.oneTimePurchase({
        amount: data['amount'],
        currency: data['currency code'],
        paymentFlowId: data['payment flow id'],
        affiliation: data['initiator'],
      });
    } else if (data['flow type'] === 'subscription') {
      ga.subscriptionPurchase({
        amount: data['amount'],
        currency: data['currency code'],
        paymentFlowId: data['payment flow id'],
      });
    }
  }
}

type setUserPropertiesProps = {
  id?: string | number;
  properties?: {
    [name: string]: string | number | boolean | unknown[] | Record<string, unknown>;
  };
};

export const setUserProperties = ({ id, properties }: setUserPropertiesProps) => {
  try {
    const { amplitude, amplitudeProxy, shouldQueue, queue } = state;
    if (!amplitude || !amplitudeProxy) {
      if (shouldQueue) {
        queue.push({ name: 'setUserProperties', params: [{ id, properties }] });
      }
      return;
    }

    if (id) {
      const stringId = typeof id === 'number' ? id.toString() : id;
      amplitude.setUserId(stringId);
      amplitudeProxy.setUserId(stringId);
    }
    if (properties) {
      const identify = new amplitude.Identify();
      Object.keys(properties).forEach((key) => {
        identify.set(toLowerCase(key), toLowerCase(properties[key]));
      });
      amplitude.identify(identify);

      const identifyProxy = new amplitudeProxy.Identify();
      Object.keys(properties).forEach((key) => {
        identifyProxy.set(toLowerCase(key), toLowerCase(properties[key]));
      });
      amplitudeProxy.identify(identifyProxy);
    }
  } catch (error) {
    captureException(error);
  }
};

export function trackCheckoutWidgetSwitchToggle({
  campaign,
  initiator,
  paymentFlowId,
  paymentType,
  provider,
}: {
  initiator: string;
  campaign?: string;
  paymentFlowId?: string;
  provider?: 'stripe' | 'klarna';
  paymentType: PaymentType;
}) {
  sendEvent(EVENTS.supportChangePaymentType.name, {
    campaign,
    initiator,
    'payment flow id': paymentFlowId,
    'payment provider': provider,
    'payment type': paymentType,
  });
}

export function trackInitiatePayment({
  initiator,
  campaign,
  impacterIds,
  categoryId,
  categoryName,
  impacterNames,
  projectId,
  paymentFlowId = uuidv4(),
  flowType,
  provider,
}: {
  initiator: string;
  campaign?: string;
  impacterIds?: Array<string | number>;
  categoryId?: string;
  categoryName?: string;
  impacterNames?: Array<string>;
  projectId?: number;
  paymentFlowId?: string;
  flowType?: string;
  provider?: 'stripe' | 'klarna';
}) {
  // Fire and forget event (to enable returning the generated paymentFlowId quickly)
  sendEvent(EVENTS.supportClickSupport.name, {
    initiator,
    campaign,
    'support type': 'one time',
    'flow type': flowType,
    'impacter ids': impacterIds,
    'impacter names': impacterNames,
    'category id': categoryId,
    'category name': categoryName,
    'project id': projectId,
    'payment flow id': paymentFlowId,
    provider: provider,
  });

  return paymentFlowId;
}

function getLowerCasedData(properties?: Record<string, unknown>): Record<string, unknown> {
  const dataInLowerCase: Record<string, unknown> = {};
  if (properties) {
    for (const [key, value] of Object.entries(properties)) {
      dataInLowerCase[key] = toLowerCase(value);
    }
  }
  return dataInLowerCase;
}

type Options = {
  addNetworkAnalytics?: boolean;
  logServerSide?: boolean;
  addUtmTags?: boolean;
};

export function sendEventTYPED(
  event: AmplitudeEvent,
  { addNetworkAnalytics = false, logServerSide = false, addUtmTags = true }: Options = {
    addNetworkAnalytics: false,
    logServerSide: false,
    addUtmTags: false,
  },
) {
  const { amplitude, shouldQueue, queue } = state;
  if (!amplitude) {
    if (shouldQueue) {
      queue.push({ name: 'sendEventTYPED', params: [event, { addNetworkAnalytics }] });
    }
    return;
  }

  if (
    process.env.NODE_ENV === 'test' ||
    (typeof window !== 'undefined' && EXCLUDE_FROM_TRACKING.has(window.location.pathname))
  ) {
    return;
  }

  try {
    // Convert string-values to lowercase due to Amplitude being case-sensitive (will not handle nested objects)
    const dataInLowerCase: Record<string, unknown> = getLowerCasedData(event.properties);

    if (addNetworkAnalytics) {
      dataInLowerCase.network = getNetworkAnalytics();
    }

    if (addUtmTags) {
      dataInLowerCase.queryUtmTags = getUtmTagsFromQuery(Router);
    }

    if (isBrowser()) {
      logAmplitudeEvent(event.name, dataInLowerCase);
    } else if (logServerSide) {
      return logAmplitudeServerEvent(event.name, dataInLowerCase);
    } else {
      return;
    }

    logAdjustEvent(event.name, dataInLowerCase);
    logGAEvent(event.name, dataInLowerCase);

    if (event.name === EVENT_NAME.LOGOUT) {
      amplitude.setUserId(null); // Reset user id
      amplitude.regenerateDeviceId(); // Generate a new device id
    }
  } catch (error) {
    // @ts-expect-error FIXME: handle error types explicitly for improved error handling
    captureMessage('trackAmplitude error', error.message);
  }
}

/**
 *
 * Simplified sendEvent through proxy for EXPERIMENT
 * Only use if you know you should use it
 */
export function sendEventProxy(event: AmplitudeEvent) {
  const { amplitudeProxy, shouldQueue, queue } = state;
  if (!amplitudeProxy) {
    if (shouldQueue) {
      queue.push({ name: 'sendEventProxy', params: [event] });
    }
    return;
  }

  if (
    process.env.NODE_ENV === 'test' ||
    (typeof window !== 'undefined' && EXCLUDE_FROM_TRACKING.has(window.location.pathname))
  ) {
    return;
  }

  try {
    // Convert string-values to lowercase due to Amplitude being case-sensitive (will not handle nested objects)
    const dataInLowerCase: Record<string, unknown> = getLowerCasedData(event.properties);

    if (isBrowser()) {
      amplitudeProxy.logEvent(event.name, { ...dataInLowerCase, product: 'milkywire.com' });
    } else {
      return;
    }
  } catch (error) {
    // @ts-expect-error FIXME: handle error types explicitly for improved error handling
    captureException(new SentryError('amplitude proxy send event error', error));
  }
}

export function sendServerEvent(event: AmplitudeEvent, addNetworkAnalytics = false) {
  if (process.env.NODE_ENV === 'test') {
    return;
  }
  try {
    // Convert string-values to lowercase due to Amplitude being case-sensitive (will not handle nested objects)
    const dataInLowerCase = getLowerCasedData(event.properties);

    if (addNetworkAnalytics) {
      dataInLowerCase.network = getNetworkAnalytics();
    }

    logAmplitudeServerEvent(event.name, dataInLowerCase);
  } catch (error) {
    // @ts-expect-error FIXME: handle error types explicitly for improved error handling
    captureMessage('trackAmplitude server error', error.message);
  }
}

const getNetworkAnalytics = () => {
  const { type, rtt, downlink, downlinkMax, saveData, effectiveType } = getNetworkInfo();
  return {
    'connection type': type,
    'connection effective type': effectiveType,
    'connection downlink': downlink,
    'connection rtt': rtt,
    'connection downlink max': downlinkMax,
    'connection save data': saveData,
  };
};

export const getDeviceId = () => state.amplitude?.options.deviceId;
